import Phaser from 'phaser';
import { createInputFrame, createMvpBattle, getMvpCharacterDefinition } from '../../core';
import type { BattleCore, BattleSnapshot, InputFrame, RoundResult } from '../../core';
import { AudioAdapter } from '../audio/audio-adapter';
import { airborneInvincibilityAlpha } from '../battle/animation-resolver';
import { CombatDiagnosticOverlay, combatDebugEnabled, projectCombatPresentation } from '../battle/combat-diagnostic-overlay';
import { PhaserBattleAdapter } from '../battle/phaser-battle-adapter';
import { HUD_LAYOUT } from '../battle/hud';
import { CinematicController } from '../cinematic/cinematic-controller';
import { FixedStepDriver } from '../driver/fixed-step-driver';
import { GamepadInputAdapter } from '../input/gamepad-input';
import { KeyboardInputAdapter } from '../input/keyboard-input';
import { createGameSession, otherCharacter } from '../session/game-session';
import type { GameMode, MvpCharacterId } from '../session/game-session';
import { DebugOverlay } from '../debug/debug-overlay';
import { getPhase6Services } from '../../application/phase6-services';
import { AppError } from '../../infrastructure/errors/app-error';
import { BattleTrace } from '../../infrastructure/trace/battle-trace';
import type { BattleTraceEntry } from '../../infrastructure/trace/battle-trace';
import { remapBinding, SETTINGS_ACTIONS } from '../../infrastructure/settings/settings-schema';
import type { AppSettings, SettingsAction } from '../../infrastructure/settings/settings-schema';
import { StickCharacterRenderer } from '../battle/stick-character-renderer';
import { resolveStickView } from '../battle/stick-figure-model';
import type { StickPresentationState } from '../battle/stick-figure-model';
import { CombatEffectLayer } from '../battle/combat-effect-layer';
import { StageRenderer } from '../battle/stage-renderer';
import { CharacterCardComponent } from '../character-select/character-card-component';
import { CHARACTER_SELECT_LAYOUT } from '../character-select/character-select-layout';
import { RESULT_LAYOUT } from '../result/result-layout';
import { ResultCardComponent } from '../result/result-card-component';
import type { ResultWinnerPlayer } from '../result/result-card-component';
import { SuperActivationOverlay } from '../super/super-activation-overlay';
import { KoAnnouncementOverlay } from '../ko/ko-announcement-overlay';

export const phase5Session = createGameSession();
let phase6KeyConfigPlayer: 'p1' | 'p2' = 'p1';

const COLORS = Object.freeze({ bg: 0x0b1020, panel: 0x172033, ink: '#e2e8f0', accent: '#62d6ff', sales: 0x38bdf8, developer: 0xf472b6 });
const textStyle = (size = 28): Phaser.Types.GameObjects.Text.TextStyle => ({ fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif', fontSize: `${size}px`, color: COLORS.ink, align: 'center' });

const heading = (scene: Phaser.Scene, value: string, subtitle?: string): void => {
  scene.cameras.main.setBackgroundColor(COLORS.bg);
  scene.add.text(640, 105, value, { ...textStyle(58), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0.5);
  if (subtitle !== undefined) scene.add.text(640, 165, subtitle, textStyle(20)).setOrigin(0.5);
};

const button = (scene: Phaser.Scene, y: number, label: string, action: () => void): Phaser.GameObjects.Text =>
  scene.add.text(640, y, label, { ...textStyle(28), backgroundColor: '#24324f', padding: { x: 28, y: 12 } })
    .setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', action);

import {
  formatKeyCode, ACTION_LABELS, GAME_TITLE, GAME_TITLE_MAIN, GAME_TITLE_SUB,
  GAME_TAGLINE, getHowToPlayControlRows, CHARACTER_SKILL_LABELS,
} from '../ui/ui-labels';

export class BootScene extends Phaser.Scene {
  public constructor() { super('Boot'); }
  public create(): void { this.scene.start('Loading'); }
}

export class LoadingScene extends Phaser.Scene {
  public constructor() { super('Loading'); }
  public create(): void {
    heading(this, 'OFFICE CLASH', '読み込み中…');
    const status = this.add.text(640, 310, 'ゲームデータとアセットを検証中…', textStyle(22)).setOrigin(0.5);
    const retry = button(this, 430, 'RETRY', () => { void load(); }).setVisible(false);
    const load = async (): Promise<void> => {
      retry.setVisible(false); status.setText('ゲームデータとアセットを検証中…');
      const result = getPhase6Services().content.getSnapshot().state === 'FAILED'
        ? await getPhase6Services().content.retry() : await getPhase6Services().content.load();
      if (!this.scene.isActive()) return;
      if (result.state === 'READY') this.scene.start('Title');
      else if (result.error?.category === 'CONTENT_ERROR') getPhase6Services().fatal.show(result.error);
      else { status.setText(result.error?.message ?? 'コンテンツの読み込みに失敗しました。'); retry.setVisible(true); }
    };
    void load();
  }
}

export class TitleScene extends Phaser.Scene {
  public constructor() { super('Title'); }
  public create(): void {
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.renderNightOfficeBackdrop();

    // Title Hierarchy (Left Aligned at X=140)
    this.add.text(140, 70, GAME_TITLE_MAIN, { ...textStyle(56), fontStyle: 'bold', color: '#f8fafc' }).setOrigin(0, 0);
    this.add.text(140, 138, GAME_TITLE_SUB, { ...textStyle(22), fontStyle: 'bold', color: '#62d6ff' }).setOrigin(0, 0);
    this.add.text(140, 178, GAME_TAGLINE, { ...textStyle(15), color: '#94a3b8' }).setOrigin(0, 0);

    // Menu list
    const menuItems = [
      { label: 'CPU対戦', action: () => this.startMode('VS_CPU') },
      { label: 'ローカル2人対戦', action: () => this.startMode('LOCAL_2P') },
      { label: '遊び方', action: () => this.scene.start('HowToPlay') },
      { label: '設定', action: () => this.scene.start('Settings') },
      { label: 'クレジット', action: () => this.scene.start('Credits') },
    ];

    menuItems.forEach((item, index) => {
      this.createMenuItem(140, 245 + index * 65, item.label, item.action);
    });

    void getPhase6Services().ads.show('TITLE');
    this.input.keyboard?.on('keydown-ONE', () => this.startMode('VS_CPU'));
    this.input.keyboard?.on('keydown-TWO', () => this.startMode('LOCAL_2P'));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.removeAllListeners();
      getPhase6Services().ads.hide();
    });
  }

  private createMenuItem(x: number, y: number, label: string, action: () => void): void {
    const width = 360;
    const height = 48;
    const bg = this.add.graphics();
    const cursor = this.add.text(x + 16, y + height / 2, '▶', { ...textStyle(18), fontStyle: 'bold', color: '#38bdf8' })
      .setOrigin(0, 0.5).setAlpha(0);
    const text = this.add.text(x + 42, y + height / 2, label, { ...textStyle(22), fontStyle: 'bold', color: '#e2e8f0' })
      .setOrigin(0, 0.5);

    const drawBox = (hover: boolean): void => {
      bg.clear();
      bg.fillStyle(hover ? 0x24324f : 0x172033, 0.85);
      bg.fillRoundedRect(x, y, width, height, 4);
      bg.lineStyle(1.5, hover ? 0x38bdf8 : 0x334155, 1);
      bg.strokeRoundedRect(x, y, width, height, 4);
      if (hover) {
        bg.fillStyle(0x38bdf8, 1);
        bg.fillRect(x, y + 4, 3.5, height - 8);
      }
    };
    drawBox(false);

    const hitArea = this.add.rectangle(x + width / 2, y + height / 2, width, height, 0x000000, 0)
      .setInteractive({ useHandCursor: true });

    hitArea.on('pointerover', () => {
      drawBox(true);
      cursor.setAlpha(1);
      text.setColor('#38bdf8');
    });
    hitArea.on('pointerout', () => {
      drawBox(false);
      cursor.setAlpha(0);
      text.setColor('#e2e8f0');
    });
    hitArea.on('pointerdown', action);
  }

  private renderNightOfficeBackdrop(): void {
    const g = this.add.graphics().setDepth(-10);

    // Wall base
    g.fillStyle(0x0a0f1d, 1);
    g.fillRect(0, 0, 1280, 620);

    // Window structure (Right side, X: 640..1180, Y: 50..400)
    g.fillStyle(0x040814, 1);
    g.fillRect(640, 50, 540, 350);

    // Distant city skyline behind window
    g.fillStyle(0x0a1224, 1);
    const buildings = [
      { x: 650, y: 150, w: 75, h: 250 },
      { x: 735, y: 110, w: 90, h: 290 },
      { x: 835, y: 180, w: 70, h: 220 },
      { x: 915, y: 90, w: 105, h: 310 },
      { x: 1030, y: 140, w: 85, h: 260 },
      { x: 1125, y: 190, w: 50, h: 210 },
    ];
    for (const b of buildings) g.fillRect(b.x, b.y, b.w, b.h);

    // Distant lights
    g.fillStyle(0xfacc15, 0.35);
    g.fillRect(755, 140, 6, 8);
    g.fillRect(780, 190, 6, 8);
    g.fillRect(945, 120, 8, 10);
    g.fillRect(975, 170, 8, 10);
    g.fillStyle(0x38bdf8, 0.3);
    g.fillRect(675, 200, 6, 8);
    g.fillRect(950, 220, 8, 10);
    g.fillRect(1055, 180, 6, 8);

    // Window frames
    g.lineStyle(3, 0x172238, 1);
    g.strokeRect(640, 50, 540, 350);
    g.lineBetween(820, 50, 820, 400);
    g.lineBetween(1000, 50, 1000, 400);
    g.lineBetween(640, 225, 1180, 225);

    // Wall Clock at right wall (X: 1230, Y: 100) showing 02:00
    this.renderClock(g, 1230, 100, 22);

    // Right-side Desk (X: 720..1120, Y: 460..620)
    // Chair
    g.fillStyle(0x0a101d, 0.9);
    g.fillRoundedRect(840, 410, 34, 50, 4);
    g.fillRoundedRect(845, 395, 24, 12, 3);
    g.fillRect(855, 460, 4, 30);

    // Desk top
    g.fillStyle(0x111c2e, 0.9);
    g.fillRect(720, 485, 380, 14);
    g.lineStyle(1.5, 0x1e2d44, 0.8);
    g.strokeRect(720, 485, 380, 14);
    // Desk legs
    g.fillStyle(0x0d1524, 1);
    g.fillRect(730, 499, 8, 121);
    g.fillRect(1082, 499, 8, 121);

    // Monitor 1 (Left, X: 760)
    g.fillStyle(0x080d17, 1);
    g.fillRect(760, 425, 60, 45);
    g.lineStyle(1.5, 0x1e293b, 1);
    g.strokeRect(760, 425, 60, 45);
    g.fillRect(787, 470, 6, 15);
    // Glow
    g.fillStyle(0x081d33, 0.8);
    g.fillRect(764, 429, 52, 37);
    g.fillStyle(0x38bdf8, 0.25);
    g.fillRect(770, 435, 28, 2);
    g.fillRect(770, 442, 38, 2);
    g.fillRect(770, 449, 22, 2);
    g.fillRect(770, 456, 32, 2);

    // Monitor 2 (Right, X: 840)
    g.fillStyle(0x080d17, 1);
    g.fillRect(840, 425, 60, 45);
    g.lineStyle(1.5, 0x1e293b, 1);
    g.strokeRect(840, 425, 60, 45);
    g.fillRect(867, 470, 6, 15);
    // Glow
    g.fillStyle(0x091c29, 0.8);
    g.fillRect(844, 429, 52, 37);
    g.fillStyle(0x34d399, 0.22);
    g.fillRect(850, 435, 34, 2);
    g.fillRect(850, 442, 20, 2);
    g.fillRect(850, 449, 40, 2);
    g.fillRect(850, 456, 26, 2);

    // Floor Base (Y: 620..720)
    g.fillStyle(0x131c2d, 1);
    g.fillRect(0, 612, 1280, 8);
    g.lineStyle(2.5, 0x3b4c68, 1);
    g.lineBetween(0, 620, 1280, 620);
    g.fillStyle(0x161e2e, 1);
    g.fillRect(0, 620, 1280, 100);
    g.lineStyle(1, 0x1f2b3e, 0.5);
    for (let x = 0; x < 1280; x += 80) g.lineBetween(x, 620, x - 40, 720);
    g.lineBetween(0, 670, 1280, 670);
  }

  private renderClock(g: Phaser.GameObjects.Graphics, x: number, y: number, radius: number): void {
    g.fillStyle(0x172238, 1);
    g.fillCircle(x, y, radius);
    g.lineStyle(2, 0x334155, 1);
    g.strokeCircle(x, y, radius);
    g.fillStyle(0x0f172a, 1);
    g.fillCircle(x, y, radius - 3);
    g.fillStyle(0x94a3b8, 0.8);
    g.fillRect(x - 1, y - radius + 4, 2, 4);
    g.fillRect(x + radius - 8, y - 1, 4, 2);
    g.fillRect(x - 1, y + radius - 8, 2, 4);
    g.fillRect(x - radius + 4, y - 1, 4, 2);
    g.lineStyle(2, 0xe2e8f0, 1);
    g.lineBetween(x, y, x, y - radius + 6);
    const hourLen = radius * 0.55;
    g.lineStyle(2.5, 0x38bdf8, 1);
    g.lineBetween(x, y, x + hourLen * 0.866, y - hourLen * 0.5);
    g.fillStyle(0x38bdf8, 1);
    g.fillCircle(x, y, 2.5);
  }

  private startMode(mode: GameMode): void { phase5Session.mode = mode; this.scene.start('CharacterSelect'); }
}

const sceneButton = (scene: Phaser.Scene, x: number, y: number, label: string, action: () => void, fontSize = 26): Phaser.GameObjects.Text =>
  scene.add.text(x, y, label, { ...textStyle(fontSize), backgroundColor: '#24324f', padding: { x: 20, y: 10 } })
    .setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', action);

export class HowToPlayScene extends Phaser.Scene {
  public constructor() { super('HowToPlay'); }
  public create(): void {
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.add.text(640, 45, '遊び方', { ...textStyle(38), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0.5);
    void getPhase6Services().ads.show('HOW_TO_PLAY');

    const settings = getPhase6Services().settings.get();

    // 1P 操作 (左カラム: X=220)
    this.add.text(220, 85, '【 1P 操作 】', { ...textStyle(20), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0, 0);
    this.add.text(220, 115, getHowToPlayControlRows(settings.keyBindings.p1).join('\n'), {
      ...textStyle(15), lineSpacing: 4, align: 'left',
    }).setOrigin(0, 0);

    // 2P 操作 (右カラム: X=740)
    this.add.text(740, 85, '【 2P 操作 】', { ...textStyle(20), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0, 0);
    this.add.text(740, 115, getHowToPlayControlRows(settings.keyBindings.p2).join('\n'), {
      ...textStyle(15), lineSpacing: 4, align: 'left',
    }).setOrigin(0, 0);

    // キャラクター固有技 (中央見出し + 2カラム)
    this.add.text(640, 360, '【 キャラクター固有技 】', { ...textStyle(20), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0.5);

    // Sales
    this.add.text(220, 395, CHARACTER_SKILL_LABELS.sales.displayName, { ...textStyle(17), fontStyle: 'bold', color: '#38bdf8' }).setOrigin(0, 0);
    this.add.text(220, 425, [
      `スキル1 : ${CHARACTER_SKILL_LABELS.sales.skill1}`,
      `スキル2 : ${CHARACTER_SKILL_LABELS.sales.skill2}`,
      `必殺技  : ${CHARACTER_SKILL_LABELS.sales.super}`,
    ].join('\n'), { ...textStyle(15), lineSpacing: 4, align: 'left' }).setOrigin(0, 0);

    // Developer
    this.add.text(740, 395, CHARACTER_SKILL_LABELS.developer.displayName, { ...textStyle(17), fontStyle: 'bold', color: '#f472b6' }).setOrigin(0, 0);
    this.add.text(740, 425, [
      `スキル1 : ${CHARACTER_SKILL_LABELS.developer.skill1}`,
      `スキル2 : ${CHARACTER_SKILL_LABELS.developer.skill2}`,
      `必殺技  : ${CHARACTER_SKILL_LABELS.developer.super}`,
    ].join('\n'), { ...textStyle(15), lineSpacing: 4, align: 'left' }).setOrigin(0, 0);

    // Notes
    this.add.text(640, 545, '※ 必殺技はゲージMAX（100%）時に発動可能   ※ 2人プレイ時はゲームパッド使用を推奨', {
      ...textStyle(14), color: '#94a3b8', lineSpacing: 4, align: 'center',
    }).setOrigin(0.5);

    button(this, 620, '戻る', () => this.scene.start('Title'));
    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('Title'));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.removeAllListeners();
      getPhase6Services().ads.hide();
    });
  }
}

export class CreditsScene extends Phaser.Scene {
  public constructor() { super('Credits'); }
  public create(): void {
    heading(this, 'クレジット');
    getPhase6Services().ads.hide();
    this.add.text(640, 350, [
      GAME_TITLE,
      '',
      '企画・開発',
      'デジガーデン',
      '',
      'AI開発支援',
      '複数の生成AI・コーディングAIを使用',
      '',
      '制作',
      'promptlab-games.com',
      '',
      'このゲームは、「AIと一緒に格闘ゲームを作ったらどうなるか」',
      'を検証するプロジェクトとして制作されています。',
      '',
      'MVPではビジュアル制作を簡略化し、棒人間による表現を採用しています。',
    ].join('\n'), { ...textStyle(17), lineSpacing: 6 }).setOrigin(0.5);
    button(this, 630, '戻る', () => this.scene.start('Title'));
    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('Title'));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.input.keyboard?.removeAllListeners());
  }
}

const clampVolume = (value: number): number => Math.min(1, Math.max(0, Math.round(value * 10) / 10));

export class SettingsScene extends Phaser.Scene {
  private unsubscribe: () => void = () => undefined;
  private valueText!: Phaser.GameObjects.Text;
  private feedback!: Phaser.GameObjects.Text;
  private resetDialogContainer!: Phaser.GameObjects.Container;
  public constructor() { super('Settings'); }
  public create(): void {
    const service = getPhase6Services().settings;
    getPhase6Services().ads.hide();
    heading(this, '設定');

    this.valueText = this.add.text(640, 195, '', { ...textStyle(20), lineSpacing: 8 }).setOrigin(0.5);

    // BGM Buttons
    sceneButton(this, 500, 260, 'BGM音量を下げる', () => {
      service.update({ audio: { bgmVolume: clampVolume(service.get().audio.bgmVolume - 0.1) } });
    }, 22);
    sceneButton(this, 780, 260, 'BGM音量を上げる', () => {
      service.update({ audio: { bgmVolume: clampVolume(service.get().audio.bgmVolume + 0.1) } });
    }, 22);

    // SE Buttons
    sceneButton(this, 500, 320, '効果音音量を下げる', () => {
      service.update({ audio: { seVolume: clampVolume(service.get().audio.seVolume - 0.1) } });
    }, 22);
    sceneButton(this, 780, 320, '効果音音量を上げる', () => {
      service.update({ audio: { seVolume: clampVolume(service.get().audio.seVolume + 0.1) } });
    }, 22);

    // Mute Button
    button(this, 380, 'ミュート切替', () => {
      service.update({ audio: { muted: !service.get().audio.muted } });
    });

    // Key Config Buttons
    sceneButton(this, 500, 440, '1Pキー設定', () => { phase6KeyConfigPlayer = 'p1'; this.scene.start('KeyConfig'); }, 24);
    sceneButton(this, 780, 440, '2Pキー設定', () => { phase6KeyConfigPlayer = 'p2'; this.scene.start('KeyConfig'); }, 24);

    // Reset Button
    button(this, 505, '初期設定に戻す', () => {
      this.resetDialogContainer.setVisible(true);
    });

    // Back Button
    button(this, 570, '戻る', () => this.scene.start('Title'));

    this.feedback = this.add.text(640, 630, '', { ...textStyle(17), color: '#fca5a5' }).setOrigin(0.5);

    // Reset Confirmation Dialog (Modal)
    this.resetDialogContainer = this.add.container(640, 360).setDepth(200).setVisible(false);
    const dimBg = this.add.rectangle(0, 0, 1280, 720, 0x000000, 0.75).setInteractive();
    const dialogBox = this.add.rectangle(0, 0, 480, 220, 0x172033, 0.98);
    const dialogBorder = this.add.graphics();
    dialogBorder.lineStyle(3, 0x62d6ff, 1);
    dialogBorder.strokeRect(-240, -110, 480, 220);
    const confirmPrompt = this.add.text(0, -45, '初期設定に戻しますか？', { ...textStyle(24), fontStyle: 'bold' }).setOrigin(0.5);
    const yesBtn = this.add.text(-90, 40, 'はい', { ...textStyle(24), backgroundColor: '#ef4444', padding: { x: 28, y: 10 } })
      .setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', () => {
        service.reset();
        this.resetDialogContainer.setVisible(false);
        this.feedback.setText('初期設定に戻しました。');
      });
    const noBtn = this.add.text(90, 40, 'いいえ', { ...textStyle(24), backgroundColor: '#24324f', padding: { x: 28, y: 10 } })
      .setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', () => {
        this.resetDialogContainer.setVisible(false);
        this.feedback.setText('初期化をキャンセルしました。');
      });
    this.resetDialogContainer.add([dimBg, dialogBox, dialogBorder, confirmPrompt, yesBtn, noBtn]);

    const render = (settings: AppSettings): void => {
      const bgmPercent = Math.round(settings.audio.bgmVolume * 100);
      const sePercent = Math.round(settings.audio.seVolume * 100);
      const mutedStr = settings.audio.muted ? 'ON' : 'OFF';
      this.valueText.setText(`BGM音量：${bgmPercent}%   効果音音量：${sePercent}%   ミュート：${mutedStr}`);
    };
    render(service.get());
    this.unsubscribe = service.subscribe(render);

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.resetDialogContainer.visible) {
        this.resetDialogContainer.setVisible(false);
        this.feedback.setText('初期化をキャンセルしました。');
      } else {
        this.scene.start('Title');
      }
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe();
      this.input.keyboard?.removeAllListeners();
    });
  }
}

export class KeyConfigScene extends Phaser.Scene {
  private feedback!: Phaser.GameObjects.Text;
  private unsubscribe: () => void = () => undefined;
  private captureHandler: ((event: KeyboardEvent) => void) | null = null;
  public constructor() { super('KeyConfig'); }
  public create(): void {
    const settings = getPhase6Services().settings;
    getPhase6Services().ads.hide();
    this.cameras.main.setBackgroundColor(COLORS.bg);

    const playerLabel = phase6KeyConfigPlayer === 'p1' ? '1P' : '2P';
    this.add.text(640, 50, `${playerLabel} キー設定`, { ...textStyle(40), fontStyle: 'bold', color: COLORS.accent }).setOrigin(0.5);
    this.add.text(640, 95, '設定したい項目をクリックし、キーを押してください（Escでキャンセル）', textStyle(17)).setOrigin(0.5);

    const rows = SETTINGS_ACTIONS.map((action, index) => this.add.text(640, 145 + index * 40, '', {
      ...textStyle(17), backgroundColor: '#24324f', padding: { x: 18, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.capture(action)));

    const render = (value: AppSettings): void => rows.forEach((row, index) => {
      const action = SETTINGS_ACTIONS[index]!;
      const label = ACTION_LABELS[action] ?? action;
      const formattedKeys = value.keyBindings[phase6KeyConfigPlayer][action].map(formatKeyCode).join(' / ');
      row.setText(`${label.padEnd(8, '　')} : ${formattedKeys}`);
    });

    this.feedback = this.add.text(640, 565, '', { ...textStyle(16), color: '#fca5a5' }).setOrigin(0.5);
    button(this, 625, '戻る', () => this.scene.start('Settings'));
    render(settings.get());
    this.unsubscribe = settings.subscribe(render);

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.captureHandler === null) this.scene.start('Settings');
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe();
      if (this.captureHandler !== null) this.input.keyboard?.off('keydown', this.captureHandler);
      this.captureHandler = null;
      this.input.keyboard?.removeAllListeners();
    });
  }

  private capture(action: SettingsAction): void {
    const label = ACTION_LABELS[action] ?? action;
    this.feedback.setText(`${label}：キーを押してください（Escでキャンセル）`);
    if (this.captureHandler !== null) this.input.keyboard?.off('keydown', this.captureHandler);
    this.captureHandler = (event: KeyboardEvent): void => {
      this.captureHandler = null;
      if (event.code === 'Escape') { this.feedback.setText('設定をキャンセルしました。'); return; }
      const service = getPhase6Services().settings;
      try {
        const updated = remapBinding(service.get(), phase6KeyConfigPlayer, action, event.code);
        service.update({ keyBindings: updated.keyBindings });
        this.feedback.setText(`${formatKeyCode(event.code)} を割り当てました。`);
      } catch (error) { this.feedback.setText(error instanceof Error ? error.message : '無効なキーです。'); }
    };
    this.input.keyboard?.once('keydown', this.captureHandler);
  }
}

export class CharacterSelectScene extends Phaser.Scene {
  private p1: MvpCharacterId = 'sales';
  private p2: MvpCharacterId = 'developer';
  private localStep: 'P1_SELECT' | 'P2_SELECT' | 'READY' = 'P1_SELECT';

  private subtitleText!: Phaser.GameObjects.Text;
  private statusSummaryText!: Phaser.GameObjects.Text;
  private vsText!: Phaser.GameObjects.Text;
  private salesCard!: CharacterCardComponent;
  private devCard!: CharacterCardComponent;
  private mainButtonText!: Phaser.GameObjects.Text;
  private mainButtonBg!: Phaser.GameObjects.Graphics;
  private backButtonText!: Phaser.GameObjects.Text;
  private backButtonBg!: Phaser.GameObjects.Graphics;

  public constructor() { super('CharacterSelect'); }

  public create(): void {
    this.localStep = 'P1_SELECT';

    this.cameras.main.setBackgroundColor(CHARACTER_SELECT_LAYOUT.colors.bg);
    this.renderNightOfficeBackdrop();

    void getPhase6Services().ads.show('CHARACTER_SELECT');

    const layout = CHARACTER_SELECT_LAYOUT;

    // Header Title & Guidance
    this.add.text(layout.header.title.x, layout.header.title.y, 'キャラクター選択', {
      ...textStyle(46),
      fontStyle: 'bold',
      color: '#f8fafc',
    }).setOrigin(0.5);

    this.subtitleText = this.add.text(layout.header.subtitle.x, layout.header.subtitle.y, '', {
      ...textStyle(17),
      color: '#94a3b8',
    }).setOrigin(0.5);

    this.statusSummaryText = this.add.text(layout.header.statusSummary.x, layout.header.statusSummary.y, '', {
      ...textStyle(16),
      fontStyle: 'bold',
      color: '#fde047',
    }).setOrigin(0.5);

    // Center VS Display
    this.vsText = this.add.text(layout.cards.vs.x, layout.cards.vs.y, 'VS', {
      ...textStyle(38),
      fontStyle: 'bold',
      color: '#475569',
    }).setOrigin(0.5);

    // Sales Card (Left: X=350, Y=375)
    this.salesCard = new CharacterCardComponent(
      this,
      'sales',
      layout.cards.sales.centerX,
      layout.cards.sales.centerY,
      () => this.selectCharacter('sales'),
    );

    // Developer Card (Right: X=930, Y=375)
    this.devCard = new CharacterCardComponent(
      this,
      'developer',
      layout.cards.developer.centerX,
      layout.cards.developer.centerY,
      () => this.selectCharacter('developer'),
    );

    // Primary Action Button (対戦開始 / 1P 決定 / 2P 決定)
    this.createPrimaryButton(layout.actions.primary.x, layout.actions.primary.y, () => this.handleMainAction());

    // Secondary Action Button (戻る / 1P選択に戻る / 選び直す)
    this.createSecondaryButton(layout.actions.secondary.x, layout.actions.secondary.y, () => this.handleBackAction());

    this.updateSelectionView();

    this.input.keyboard?.on('keydown-ESC', () => this.handleBackAction());
    this.input.keyboard?.on('keydown-ENTER', () => this.handleMainAction());
    this.input.keyboard?.on('keydown-SPACE', () => this.handleMainAction());
    this.input.keyboard?.on('keydown-LEFT', () => this.selectCharacter('sales'));
    this.input.keyboard?.on('keydown-A', () => this.selectCharacter('sales'));
    this.input.keyboard?.on('keydown-RIGHT', () => this.selectCharacter('developer'));
    this.input.keyboard?.on('keydown-D', () => this.selectCharacter('developer'));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.removeAllListeners();
      getPhase6Services().ads.hide();
    });
  }

  private createPrimaryButton(x: number, y: number, action: () => void): void {
    const width = 240;
    const height = 40;
    this.mainButtonBg = this.add.graphics();
    this.mainButtonText = this.add.text(x, y, '対戦開始', {
      ...textStyle(20),
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5);

    const draw = (hover: boolean): void => {
      this.mainButtonBg.clear();
      this.mainButtonBg.fillStyle(hover ? 0x2563eb : 0x1e3a8a, 0.95);
      this.mainButtonBg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 4);
      this.mainButtonBg.lineStyle(1.5, hover ? 0x60a5fa : 0x38bdf8, 1);
      this.mainButtonBg.strokeRoundedRect(x - width / 2, y - height / 2, width, height, 4);
    };
    draw(false);

    const hitArea = this.add.rectangle(x, y, width, height, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hitArea.on('pointerover', () => draw(true));
    hitArea.on('pointerout', () => draw(false));
    hitArea.on('pointerdown', action);
  }

  private createSecondaryButton(x: number, y: number, action: () => void): void {
    const width = 180;
    const height = 32;
    this.backButtonBg = this.add.graphics();
    this.backButtonText = this.add.text(x, y, '戻る', {
      ...textStyle(15),
      color: '#cbd5e1',
    }).setOrigin(0.5);

    const draw = (hover: boolean): void => {
      this.backButtonBg.clear();
      this.backButtonBg.fillStyle(hover ? 0x1e293b : 0x0f172a, 0.9);
      this.backButtonBg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 4);
      this.backButtonBg.lineStyle(1.2, hover ? 0x64748b : 0x334155, 1);
      this.backButtonBg.strokeRoundedRect(x - width / 2, y - height / 2, width, height, 4);
    };
    draw(false);

    const hitArea = this.add.rectangle(x, y, width, height, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hitArea.on('pointerover', () => draw(true));
    hitArea.on('pointerout', () => draw(false));
    hitArea.on('pointerdown', action);
  }

  private selectCharacter(id: MvpCharacterId): void {
    if (phase5Session.mode === 'VS_CPU') {
      this.p1 = id;
      this.p2 = otherCharacter(id);
    } else {
      if (this.localStep === 'P1_SELECT') {
        this.p1 = id;
      } else if (this.localStep === 'P2_SELECT') {
        this.p2 = id;
      }
    }
    this.updateSelectionView();
  }

  private handleMainAction(): void {
    if (phase5Session.mode === 'VS_CPU') {
      this.confirm();
    } else {
      if (this.localStep === 'P1_SELECT') {
        this.localStep = 'P2_SELECT';
        this.updateSelectionView();
      } else if (this.localStep === 'P2_SELECT') {
        this.localStep = 'READY';
        this.updateSelectionView();
      } else if (this.localStep === 'READY') {
        this.confirm();
      }
    }
  }

  private handleBackAction(): void {
    if (phase5Session.mode === 'VS_CPU') {
      this.scene.start('Title');
    } else {
      if (this.localStep === 'P1_SELECT') {
        this.scene.start('Title');
      } else if (this.localStep === 'P2_SELECT') {
        this.localStep = 'P1_SELECT';
        this.updateSelectionView();
      } else if (this.localStep === 'READY') {
        this.localStep = 'P1_SELECT';
        this.updateSelectionView();
      }
    }
  }

  private updateSelectionView(): void {
    const isCpu = phase5Session.mode === 'VS_CPU';

    if (isCpu) {
      this.subtitleText.setText('1Pを選択（CPUはもう一方のキャラクターになります）');
      this.statusSummaryText.setText('');
      this.mainButtonText.setText('対戦開始');
      this.backButtonText.setText('戻る');
      this.vsText.setColor('#62d6ff');

      const salesSelected = this.p1 === 'sales';
      this.salesCard.updateState({
        status: salesSelected ? '1P_SELECTING' : 'CPU',
        isPrimary: salesSelected,
      });
      this.devCard.updateState({
        status: salesSelected ? 'CPU' : '1P_SELECTING',
        isPrimary: !salesSelected,
      });
    } else {
      const p1Label = this.p1 === 'sales' ? 'Sales' : 'Developer';
      const p2Label = this.p2 === 'sales' ? 'Sales' : 'Developer';

      if (this.localStep === 'P1_SELECT') {
        this.subtitleText.setText('1Pのキャラクターを選択してください');
        this.statusSummaryText.setText('1P：選択中');
        this.mainButtonText.setText('1P 決定');
        this.backButtonText.setText('戻る');
        this.vsText.setColor('#475569');

        const salesSelected = this.p1 === 'sales';
        this.salesCard.updateState({
          status: salesSelected ? '1P_SELECTING' : 'UNSELECTED',
          isPrimary: salesSelected,
        });
        this.devCard.updateState({
          status: salesSelected ? 'UNSELECTED' : '1P_SELECTING',
          isPrimary: !salesSelected,
        });
      } else if (this.localStep === 'P2_SELECT') {
        this.subtitleText.setText('2Pのキャラクターを選択してください');
        this.statusSummaryText.setText(`1P：${p1Label} (確定)   2P：選択中`);
        this.mainButtonText.setText('2P 決定');
        this.backButtonText.setText('1P選択に戻る');
        this.vsText.setColor('#94a3b8');

        const salesIsP1 = this.p1 === 'sales';
        const salesIsP2 = this.p2 === 'sales';
        const devIsP1 = this.p1 === 'developer';
        const devIsP2 = this.p2 === 'developer';

        if (salesIsP1 && salesIsP2) {
          this.salesCard.updateState({ status: '1P_LOCKED_2P_SELECTING', isPrimary: true });
          this.devCard.updateState({ status: 'UNSELECTED', isPrimary: false });
        } else if (devIsP1 && devIsP2) {
          this.salesCard.updateState({ status: 'UNSELECTED', isPrimary: false });
          this.devCard.updateState({ status: '1P_LOCKED_2P_SELECTING', isPrimary: true });
        } else if (salesIsP1 && devIsP2) {
          this.salesCard.updateState({ status: '1P_LOCKED', isPrimary: false });
          this.devCard.updateState({ status: '2P_SELECTING', isPrimary: true });
        } else {
          this.salesCard.updateState({ status: '2P_SELECTING', isPrimary: true });
          this.devCard.updateState({ status: '1P_LOCKED', isPrimary: false });
        }
      } else if (this.localStep === 'READY') {
        this.subtitleText.setText('対戦準備完了！ 対戦を開始してください');
        this.statusSummaryText.setText(`1P：${p1Label}   VS   2P：${p2Label}`);
        this.mainButtonText.setText('対戦開始');
        this.backButtonText.setText('選び直す');
        this.vsText.setColor('#62d6ff');

        const salesIsP1 = this.p1 === 'sales';
        const salesIsP2 = this.p2 === 'sales';
        const devIsP1 = this.p1 === 'developer';
        const devIsP2 = this.p2 === 'developer';

        if (salesIsP1 && salesIsP2) {
          this.salesCard.updateState({ status: 'BOTH_LOCKED', isPrimary: true });
          this.devCard.updateState({ status: 'UNSELECTED', isPrimary: false });
        } else if (devIsP1 && devIsP2) {
          this.salesCard.updateState({ status: 'UNSELECTED', isPrimary: false });
          this.devCard.updateState({ status: 'BOTH_LOCKED', isPrimary: true });
        } else if (salesIsP1 && devIsP2) {
          this.salesCard.updateState({ status: '1P_LOCKED', isPrimary: true });
          this.devCard.updateState({ status: '2P_LOCKED', isPrimary: true });
        } else {
          this.salesCard.updateState({ status: '2P_LOCKED', isPrimary: true });
          this.devCard.updateState({ status: '1P_LOCKED', isPrimary: true });
        }
      }
    }
  }

  private renderNightOfficeBackdrop(): void {
    const g = this.add.graphics().setDepth(-10);

    // Wall base
    g.fillStyle(0x070b16, 1);
    g.fillRect(0, 0, 1280, 620);

    // Window structure (Right side, X: 640..1180, Y: 40..380)
    g.fillStyle(0x030610, 1);
    g.fillRect(640, 40, 540, 340);

    // Distant city skyline behind window
    g.fillStyle(0x070d1a, 1);
    const buildings = [
      { x: 650, y: 150, w: 75, h: 230 },
      { x: 735, y: 110, w: 90, h: 270 },
      { x: 835, y: 180, w: 70, h: 200 },
      { x: 915, y: 90, w: 105, h: 290 },
      { x: 1030, y: 140, w: 85, h: 240 },
      { x: 1125, y: 190, w: 50, h: 190 },
    ];
    for (const b of buildings) g.fillRect(b.x, b.y, b.w, b.h);

    // Distant lights (Muted contrast)
    g.fillStyle(0xfacc15, 0.22);
    g.fillRect(755, 140, 6, 8);
    g.fillRect(780, 190, 6, 8);
    g.fillRect(945, 120, 8, 10);
    g.fillRect(975, 170, 8, 10);
    g.fillStyle(0x38bdf8, 0.2);
    g.fillRect(675, 200, 6, 8);
    g.fillRect(950, 220, 8, 10);
    g.fillRect(1055, 180, 6, 8);

    // Window frames
    g.lineStyle(2.5, 0x131c2e, 1);
    g.strokeRect(640, 40, 540, 340);
    g.lineBetween(820, 40, 820, 380);
    g.lineBetween(1000, 40, 1000, 380);
    g.lineBetween(640, 210, 1180, 210);

    // Wall Clock at (X: 1120, Y: 85) showing 02:00
    this.renderClock(g, 1120, 85, 20);

    // Right-side Desk Silhouette (X: 720..1120, Y: 460..620)
    g.fillStyle(0x080d17, 0.85);
    g.fillRect(720, 485, 380, 14);
    g.fillRect(730, 499, 8, 121);
    g.fillRect(1082, 499, 8, 121);

    // Monitors with faint glow
    g.fillStyle(0x050912, 1);
    g.fillRect(760, 425, 60, 45);
    g.fillRect(840, 425, 60, 45);
    g.fillStyle(0x38bdf8, 0.15);
    g.fillRect(764, 429, 52, 37);
    g.fillStyle(0x34d399, 0.15);
    g.fillRect(844, 429, 52, 37);

    // Floor Base (Y: 620..720)
    g.fillStyle(0x0f1624, 1);
    g.fillRect(0, 612, 1280, 8);
    g.lineStyle(2, 0x253347, 1);
    g.lineBetween(0, 620, 1280, 620);
    g.fillStyle(0x111726, 1);
    g.fillRect(0, 620, 1280, 100);
    g.lineStyle(1, 0x1a2436, 0.4);
    for (let x = 0; x < 1280; x += 80) g.lineBetween(x, 620, x - 40, 720);
    g.lineBetween(0, 670, 1280, 670);
  }

  private renderClock(g: Phaser.GameObjects.Graphics, x: number, y: number, radius: number): void {
    g.fillStyle(0x131c2e, 1);
    g.fillCircle(x, y, radius);
    g.lineStyle(1.5, 0x253347, 1);
    g.strokeCircle(x, y, radius);
    g.fillStyle(0x0a101d, 1);
    g.fillCircle(x, y, radius - 2.5);
    g.fillStyle(0x64748b, 0.8);
    g.fillRect(x - 1, y - radius + 3, 2, 3);
    g.fillRect(x + radius - 6, y - 1, 3, 2);
    g.fillRect(x - 1, y + radius - 6, 2, 3);
    g.fillRect(x - radius + 3, y - 1, 3, 2);
    // Minute hand pointing to 12
    g.lineStyle(1.5, 0xcbd5e1, 1);
    g.lineBetween(x, y, x, y - radius + 5);
    // Hour hand pointing to 2 (02:00)
    const hourLen = radius * 0.55;
    g.lineStyle(2, 0x38bdf8, 1);
    g.lineBetween(x, y, x + hourLen * 0.866, y - hourLen * 0.5);
    g.fillStyle(0x38bdf8, 1);
    g.fillCircle(x, y, 2);
  }

  private confirm(): void {
    phase5Session.p1CharacterId = this.p1;
    phase5Session.p2CharacterId = phase5Session.mode === 'VS_CPU' ? otherCharacter(this.p1) : this.p2;
    this.scene.start('PreBattle');
  }
}

export class PreBattleScene extends Phaser.Scene {
  public constructor() { super('PreBattle'); }
  public create(): void {
    heading(this, 'GET READY', '対戦準備中…');
    const left = getMvpCharacterDefinition(phase5Session.p1CharacterId).displayName;
    const right = getMvpCharacterDefinition(phase5Session.p2CharacterId).displayName;
    this.add.text(640, 350, `${left.toUpperCase()}     VS     ${right.toUpperCase()}`, { ...textStyle(38), fontStyle: 'bold' }).setOrigin(0.5);
    const begin = (): void => { this.scene.start('Battle'); };
    this.time.delayedCall(1_500, begin);
    this.input.keyboard?.once('keydown-ENTER', begin); this.input.keyboard?.once('keydown-SPACE', begin);
    for (const key of ['keydown-F', 'keydown-G', 'keydown-H', 'keydown-J', 'keydown-NUMPAD_ONE', 'keydown-NUMPAD_TWO']) {
      this.input.keyboard?.once(key, begin);
    }
    this.input.once('pointerdown', begin);
  }
}

const mergeInput = (first: InputFrame, second: InputFrame): InputFrame => createInputFrame(Object.fromEntries(
  Object.keys(first).map((action) => [action, first[action as keyof InputFrame] || second[action as keyof InputFrame]]),
));

export class BattleScene extends Phaser.Scene {
  private combatDiagnosticOverlay: CombatDiagnosticOverlay | null = null;
  private core!: BattleCore;
  private readonly driver = new FixedStepDriver();
  private keyboardAdapter!: KeyboardInputAdapter;
  private readonly gamepadAdapter = new GamepadInputAdapter();
  private readonly presentation = new PhaserBattleAdapter();
  private readonly cinematic = new CinematicController();
  private audio!: AudioAdapter;
  private readonly trace = new BattleTrace();
  private world!: Phaser.GameObjects.Graphics;
  private stageRenderer!: StageRenderer;
  private stickRenderer!: StickCharacterRenderer;
  private effectsGraphics!: Phaser.GameObjects.Graphics;
  private effectLayer!: CombatEffectLayer;
  private hud!: Phaser.GameObjects.Graphics;
  private timerText!: Phaser.GameObjects.Text;
  private p1GaugeLabel!: Phaser.GameObjects.Text;
  private p2GaugeLabel!: Phaser.GameObjects.Text;
  private effectText!: Phaser.GameObjects.Text;
  private superOverlay!: SuperActivationOverlay;
  private koOverlay!: KoAnnouncementOverlay;
  private hudCamera!: Phaser.Cameras.Scene2D.Camera;
  private debugOverlay!: DebugOverlay;
  private latest!: BattleSnapshot;
  private presentationElapsedMs = 0;
  private cinematicProbeCompletions = 0;
  private readonly wakingPlayers = new Set<0 | 1>();
  private isFinishingRound = false;
  public constructor() { super('Battle'); }
  public create(): void {
    const services = getPhase6Services(); services.ads.hide(); this.trace.clear();
    phase5Session.battleSerial += 1; phase5Session.roundResult = null;
    this.isFinishingRound = false;
    this.core = createMvpBattle({ mode: phase5Session.mode, p1CharacterId: phase5Session.p1CharacterId,
      p2CharacterId: phase5Session.p2CharacterId, seed: 0x0ff1ce, humanPlayer: 0 });
    this.core.transitionBattlePhase('FIGHTING'); this.latest = this.core.getSnapshot();
    if (import.meta.env.DEV && combatDebugEnabled(import.meta.env.DEV, window.location.search)) {
      this.core.enableCombatDiagnostics();
      this.combatDiagnosticOverlay = new CombatDiagnosticOverlay(() =>
        projectCombatPresentation(this.core.getCombatDiagnosticSnapshot(), this.presentationElapsedMs));
    }
    this.keyboardAdapter = new KeyboardInputAdapter(globalThis.window, services.settings);
    this.audio = new AudioAdapter(null, services.settings);
    services.fatal.registerBattle(this.core, () => { this.driver.reset(); this.cinematic.dispose(); this.presentation.dispose(); this.effectLayer.dispose(); this.trace.clear(); });
    this.world = this.add.graphics().setDepth(0);
    this.stageRenderer = new StageRenderer(this.world);
    this.stickRenderer = new StickCharacterRenderer(this.world);
    this.effectsGraphics = this.add.graphics().setDepth(50); this.effectLayer = new CombatEffectLayer(this.effectsGraphics); this.hud = this.add.graphics().setDepth(100);
    this.timerText = this.add.text(HUD_LAYOUT.timer.x, HUD_LAYOUT.timer.y, '60', { ...textStyle(36), fontStyle: 'bold', color: '#f8fafc' }).setOrigin(0.5).setDepth(101);
    const p1Badge = this.add.text(HUD_LAYOUT.p1Badge.x, HUD_LAYOUT.p1Badge.y, '1P', { ...textStyle(15), fontStyle: 'bold', color: '#38bdf8', backgroundColor: '#1e293b', padding: { x: 6, y: 2 } }).setDepth(101);
    const p1Name = this.add.text(HUD_LAYOUT.p1Name.x, HUD_LAYOUT.p1Name.y, getMvpCharacterDefinition(phase5Session.p1CharacterId).displayName, { ...textStyle(18), fontStyle: 'bold', color: '#e2e8f0' }).setDepth(101);
    const p2Badge = this.add.text(HUD_LAYOUT.p2Badge.x, HUD_LAYOUT.p2Badge.y, '2P', { ...textStyle(15), fontStyle: 'bold', color: '#fb923c', backgroundColor: '#1e293b', padding: { x: 6, y: 2 } }).setOrigin(1, 0).setDepth(101);
    const p2Name = this.add.text(HUD_LAYOUT.p2Name.x, HUD_LAYOUT.p2Name.y, getMvpCharacterDefinition(phase5Session.p2CharacterId).displayName, { ...textStyle(18), fontStyle: 'bold', color: '#e2e8f0' }).setOrigin(1, 0).setDepth(101);
    this.p1GaugeLabel = this.add.text(HUD_LAYOUT.p1GaugeLabel.x, HUD_LAYOUT.p1GaugeLabel.y, 'SUPER', { ...textStyle(13), fontStyle: 'bold', color: '#facc15' }).setDepth(101);
    this.p2GaugeLabel = this.add.text(HUD_LAYOUT.p2GaugeLabel.x, HUD_LAYOUT.p2GaugeLabel.y, 'SUPER', { ...textStyle(13), fontStyle: 'bold', color: '#facc15' }).setOrigin(1, 0).setDepth(101);
    this.effectText = this.add.text(640, 220, '', { ...textStyle(42), fontStyle: 'bold', color: '#fde047' }).setOrigin(0.5).setDepth(110);
    this.superOverlay = new SuperActivationOverlay(this);
    this.koOverlay = new KoAnnouncementOverlay(this);
    this.hudCamera = this.cameras.add(0, 0, 1_280, 720);
    this.cameras.main.ignore([this.hud, this.timerText, p1Badge, p1Name, p2Badge, p2Name, this.p1GaugeLabel, this.p2GaugeLabel]);
    this.hudCamera.ignore([this.world, this.effectsGraphics, this.effectText, this.superOverlay.container, this.koOverlay.container]);
    this.debugOverlay = new DebugOverlay(this);
    this.audio.play('BGM_BATTLE'); this.render(this.latest);
    this.input.keyboard?.on('keydown-F3', () => this.debugOverlay.toggle());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdownBattle());
  }
  public override update(_time: number, delta: number): void {
    try {
      this.presentationElapsedMs += delta;
      const cinematicUpdate = this.cinematic.update(delta);
      if (cinematicUpdate.state !== null) {
        const characterId = cinematicUpdate.state.id.includes('sales')
          ? 'sales'
          : cinematicUpdate.state.id.includes('developer')
            ? 'developer'
            : (cinematicUpdate.state.playerSlot === 1 ? phase5Session.p2CharacterId : phase5Session.p1CharacterId);
        this.superOverlay.update(cinematicUpdate.state, characterId);
      } else this.superOverlay.update(null, phase5Session.p1CharacterId);
      if (cinematicUpdate.completedId !== null) { this.core.completeCinematic(cinematicUpdate.completedId); this.cinematicProbeCompletions += 1; }
      this.driver.advance(delta, () => this.stepCore());
    } catch (error) {
      if (import.meta.env.DEV) throw error;
      this.scene.pause(); getPhase6Services().fatal.show(new AppError('CORE_INVARIANT_ERROR', 'The battle could not continue safely.', true, { cause: error }));
    }
  }
  public getSnapshot(): BattleSnapshot { return this.latest; }
  public finishForTest(result: RoundResult): void { phase5Session.roundResult = Object.freeze({ ...result }); this.scene.start('Result'); }
  public beginCinematicProbe(id: string): boolean { this.cinematicProbeCompletions = 0; return this.cinematic.start(id, 0); }
  public getCinematicProbeCompletions(): number { return this.cinematicProbeCompletions; }
  public getTrace(): readonly BattleTraceEntry[] { return this.trace.getEntries(); }
  public getVisualState(): Readonly<{ states: readonly [StickPresentationState, StickPresentationState]; attackPhases: readonly (string | null)[]; effects: readonly string[]; transientCount: number; trapCount: number }> {
    const views = [resolveStickView(this.latest, 0, this.wakingPlayers.has(0)), resolveStickView(this.latest, 1, this.wakingPlayers.has(1))] as const;
    const debug = this.core.getDebugSnapshot();
    return Object.freeze({ states: Object.freeze([views[0].pose.state, views[1].pose.state] as const),
      attackPhases: Object.freeze([views[0].pose.attackPhase, views[1].pose.attackPhase]), effects: this.effectLayer.activeKinds,
      transientCount: this.effectLayer.activeCount, trapCount: debug.boxes.filter(({ type }) => type === 'TRAP').length });
  }
  public triggerRoundEnd(result: RoundResult): void {
    if (this.isFinishingRound) return;
    this.isFinishingRound = true;
    this.superOverlay.dismiss();
    phase5Session.roundResult = Object.freeze({ ...result });
    this.koOverlay.show(result.reason);
    const delayMs = result.reason === 'KO' ? 1300 : 1100;
    this.time.delayedCall(delayMs, () => this.scene.start('Result'));
  }
  private stepCore(): void {
    const p1 = mergeInput(this.keyboardAdapter.sample(0), this.gamepadAdapter.sample(0));
    const p2 = phase5Session.mode === 'LOCAL_2P'
      ? mergeInput(this.keyboardAdapter.sample(1), this.gamepadAdapter.sample(1)) : createInputFrame();
    const result = this.core.step([p1, p2]); this.latest = result.snapshot;
    this.trace.record(result.snapshot, [p1, p2], result.events);
    const frame = this.presentation.project(result.snapshot, result.events);
    this.effectLayer.updateEvents(result.events);
    this.effectLayer.stepFrame();
    this.effectLayer.consume(frame.cues, result.snapshot, this.presentationElapsedMs);
    for (const cue of frame.cues) {
      if (cue.kind === 'WAKE') {
        const player = Number(cue.payload.player); if (player === 0 || player === 1) this.wakingPlayers.add(player);
      }
      if (cue.kind !== 'KO' && cue.kind !== 'TIME_UP' && cue.kind !== 'DRAW' && cue.kind !== 'CINEMATIC' && cue.kind !== 'EFFECT') {
        this.effectText.setText(cue.kind).setAlpha(1);
        this.tweens.add({ targets: this.effectText, alpha: 0, duration: cue.durationFrames * 1_000 / 60 });
      }
      if (cue.shakeDurationMs > 0) this.cameras.main.shake(cue.shakeDurationMs, cue.shakeIntensity);
      if (cue.kind === 'CINEMATIC') this.cinematic.start(String(cue.payload.cinematicId), Number(cue.payload.playerSlot) === 1 ? 1 : 0);
      if (cue.kind !== 'CINEMATIC' && cue.kind !== 'EFFECT') this.audio.play(cue.kind);
    }
    this.render(result.snapshot);
    if (result.snapshot.roundResult !== null && !this.isFinishingRound) {
      this.triggerRoundEnd(result.snapshot.roundResult);
    }
  }
  private render(snapshot: BattleSnapshot): void {
    for (const player of [...this.wakingPlayers]) if (snapshot.characters[player].state !== 'Knockdown') this.wakingPlayers.delete(player);
    this.stageRenderer.render(snapshot);
    const views = [resolveStickView(snapshot, 0, this.wakingPlayers.has(0)), resolveStickView(snapshot, 1, this.wakingPlayers.has(1))] as const;
    snapshot.characters.forEach((character, player) => {
      this.stickRenderer.draw(views[player as 0 | 1], character.position, character.facing,
        airborneInvincibilityAlpha(character.isAirborneInvincible, this.presentationElapsedMs), player as 0 | 1);
    });
    const debug = this.core.getDebugSnapshot(); this.effectLayer.render(snapshot, debug, views, this.presentationElapsedMs);
    const model = this.presentation.project(snapshot, []).hud;
    const p1Gauge = model.gauges[0];
    const p2Gauge = model.gauges[1];

    if (p1Gauge >= 100) this.p1GaugeLabel.setText('MAX').setColor('#fde047');
    else this.p1GaugeLabel.setText('SUPER').setColor('#facc15');

    if (p2Gauge >= 100) this.p2GaugeLabel.setText('MAX').setColor('#fde047');
    else this.p2GaugeLabel.setText('SUPER').setColor('#facc15');

    this.hud.clear()
      // Top status frames with distinct breathing room from center timer
      .fillStyle(0x0f172a, 0.85).fillRoundedRect(HUD_LAYOUT.p1Panel.x, HUD_LAYOUT.p1Panel.y, HUD_LAYOUT.p1Panel.width, HUD_LAYOUT.p1Panel.height, 4)
      .lineStyle(1.5, 0x334155, 1).strokeRoundedRect(HUD_LAYOUT.p1Panel.x, HUD_LAYOUT.p1Panel.y, HUD_LAYOUT.p1Panel.width, HUD_LAYOUT.p1Panel.height, 4)
      .fillStyle(0x0f172a, 0.85).fillRoundedRect(HUD_LAYOUT.p2Panel.x, HUD_LAYOUT.p2Panel.y, HUD_LAYOUT.p2Panel.width, HUD_LAYOUT.p2Panel.height, 4)
      .lineStyle(1.5, 0x334155, 1).strokeRoundedRect(HUD_LAYOUT.p2Panel.x, HUD_LAYOUT.p2Panel.y, HUD_LAYOUT.p2Panel.width, HUD_LAYOUT.p2Panel.height, 4)
      .fillStyle(0x0f172a, 0.9).fillRoundedRect(HUD_LAYOUT.timerPanel.x, HUD_LAYOUT.timerPanel.y, HUD_LAYOUT.timerPanel.width, HUD_LAYOUT.timerPanel.height, 4)
      .lineStyle(1.5, 0x334155, 1).strokeRoundedRect(HUD_LAYOUT.timerPanel.x, HUD_LAYOUT.timerPanel.y, HUD_LAYOUT.timerPanel.width, HUD_LAYOUT.timerPanel.height, 4)
      // HP Bars
      .fillStyle(0x1e293b, 1).fillRect(HUD_LAYOUT.p1Hp.x, HUD_LAYOUT.p1Hp.y, HUD_LAYOUT.p1Hp.width, HUD_LAYOUT.p1Hp.height)
      .lineStyle(1, 0x475569, 1).strokeRect(HUD_LAYOUT.p1Hp.x, HUD_LAYOUT.p1Hp.y, HUD_LAYOUT.p1Hp.width, HUD_LAYOUT.p1Hp.height)
      .fillStyle(0x22c55e).fillRect(HUD_LAYOUT.p1Hp.x, HUD_LAYOUT.p1Hp.y, HUD_LAYOUT.p1Hp.width * model.hpRatios[0], HUD_LAYOUT.p1Hp.height)
      .fillStyle(0x1e293b, 1).fillRect(HUD_LAYOUT.p2Hp.x, HUD_LAYOUT.p2Hp.y, HUD_LAYOUT.p2Hp.width, HUD_LAYOUT.p2Hp.height)
      .lineStyle(1, 0x475569, 1).strokeRect(HUD_LAYOUT.p2Hp.x, HUD_LAYOUT.p2Hp.y, HUD_LAYOUT.p2Hp.width, HUD_LAYOUT.p2Hp.height)
      .fillStyle(0x22c55e).fillRect(HUD_LAYOUT.p2Hp.x + HUD_LAYOUT.p2Hp.width * (1 - model.hpRatios[1]), HUD_LAYOUT.p2Hp.y, HUD_LAYOUT.p2Hp.width * model.hpRatios[1], HUD_LAYOUT.p2Hp.height)
      // Bottom Gauge Panels
      .fillStyle(0x0f172a, 0.85).fillRoundedRect(HUD_LAYOUT.p1GaugePanel.x, HUD_LAYOUT.p1GaugePanel.y, HUD_LAYOUT.p1GaugePanel.width, HUD_LAYOUT.p1GaugePanel.height, 4)
      .lineStyle(1.5, 0x334155, 1).strokeRoundedRect(HUD_LAYOUT.p1GaugePanel.x, HUD_LAYOUT.p1GaugePanel.y, HUD_LAYOUT.p1GaugePanel.width, HUD_LAYOUT.p1GaugePanel.height, 4)
      .fillStyle(0x1e293b, 1).fillRect(HUD_LAYOUT.p1Gauge.x, HUD_LAYOUT.p1Gauge.y, HUD_LAYOUT.p1Gauge.width, HUD_LAYOUT.p1Gauge.height)
      .lineStyle(1, p1Gauge >= 100 ? 0xfde047 : 0x475569, 1).strokeRect(HUD_LAYOUT.p1Gauge.x, HUD_LAYOUT.p1Gauge.y, HUD_LAYOUT.p1Gauge.width, HUD_LAYOUT.p1Gauge.height)
      .fillStyle(p1Gauge >= 100 ? 0xfde047 : 0xfacc15).fillRect(HUD_LAYOUT.p1Gauge.x, HUD_LAYOUT.p1Gauge.y, (HUD_LAYOUT.p1Gauge.width / 100) * p1Gauge, HUD_LAYOUT.p1Gauge.height)
      .fillStyle(0x0f172a, 0.85).fillRoundedRect(HUD_LAYOUT.p2GaugePanel.x, HUD_LAYOUT.p2GaugePanel.y, HUD_LAYOUT.p2GaugePanel.width, HUD_LAYOUT.p2GaugePanel.height, 4)
      .lineStyle(1.5, 0x334155, 1).strokeRoundedRect(HUD_LAYOUT.p2GaugePanel.x, HUD_LAYOUT.p2GaugePanel.y, HUD_LAYOUT.p2GaugePanel.width, HUD_LAYOUT.p2GaugePanel.height, 4)
      .fillStyle(0x1e293b, 1).fillRect(HUD_LAYOUT.p2Gauge.x, HUD_LAYOUT.p2Gauge.y, HUD_LAYOUT.p2Gauge.width, HUD_LAYOUT.p2Gauge.height)
      .lineStyle(1, p2Gauge >= 100 ? 0xfde047 : 0x475569, 1).strokeRect(HUD_LAYOUT.p2Gauge.x, HUD_LAYOUT.p2Gauge.y, HUD_LAYOUT.p2Gauge.width, HUD_LAYOUT.p2Gauge.height)
      .fillStyle(p2Gauge >= 100 ? 0xfde047 : 0xfacc15).fillRect(HUD_LAYOUT.p2Gauge.x + HUD_LAYOUT.p2Gauge.width * (1 - p2Gauge / 100), HUD_LAYOUT.p2Gauge.y, (HUD_LAYOUT.p2Gauge.width / 100) * p2Gauge, HUD_LAYOUT.p2Gauge.height);

    this.timerText.setText(String(model.timerSeconds));
    this.debugOverlay.render(debug);
    this.combatDiagnosticOverlay?.update(this.presentationElapsedMs);
  }
  private shutdownBattle(): void {
    this.superOverlay.dismiss();
    this.koOverlay.dismiss();
    this.combatDiagnosticOverlay?.dispose(); this.combatDiagnosticOverlay = null;
    this.isFinishingRound = false;
    getPhase6Services().fatal.registerBattle(null); this.trace.clear();
    this.input.keyboard?.removeAllListeners(); this.tweens.killAll(); this.time.removeAllEvents();
    this.cinematic.dispose(); this.presentation.dispose(); this.audio.dispose(); this.driver.reset(); this.keyboardAdapter.dispose();
    this.wakingPlayers.clear(); this.effectLayer.dispose(); this.debugOverlay.dispose(); this.core.dispose();
  }
}

export class ResultScene extends Phaser.Scene {
  public constructor() { super('Result'); }

  public create(): void {
    const layout = RESULT_LAYOUT;
    this.cameras.main.setBackgroundColor(layout.colors.bg);
    this.renderNightOfficeBackdrop();

    const result = phase5Session.roundResult;
    void getPhase6Services().ads.show('RESULT');

    // Header Title
    this.add.text(layout.header.title.x, layout.header.title.y, 'MATCH REPORT', {
      ...textStyle(38),
      fontStyle: 'bold',
      color: '#f8fafc',
    }).setOrigin(0.5);

    // Reason subtitle
    let reasonText = '社内戦績レポート';
    if (result) {
      if (result.winner === null || result.reason === 'DRAW') {
        reasonText = result.reason === 'KO' ? '決着 — 引き分け' : '時間切れ — 引き分け';
      } else if (result.reason === 'KO') {
        reasonText = '決着 — KO勝利';
      } else if (result.reason === 'TIME_UP') {
        reasonText = '判定 — タイムアップ勝利';
      }
    }
    this.add.text(layout.header.subtitle.x, layout.header.subtitle.y, reasonText, {
      ...textStyle(16),
      color: '#94a3b8',
    }).setOrigin(0.5);

    // Winner model resolution
    let winnerCharacterId: MvpCharacterId | null = null;
    let winnerPlayer: ResultWinnerPlayer = 'draw';

    if (result && result.winner !== null) {
      if (result.winner === 0) {
        winnerPlayer = 'p1';
        winnerCharacterId = phase5Session.p1CharacterId;
      } else {
        winnerPlayer = phase5Session.mode === 'VS_CPU' ? 'cpu' : 'p2';
        winnerCharacterId = phase5Session.p2CharacterId;
      }
    }

    // Winner Card Component
    new ResultCardComponent(this, { winnerCharacterId, winnerPlayer });

    // Action Buttons
    this.createActionButton(
      layout.actions.rematch.x,
      layout.actions.rematch.y,
      layout.actions.rematch.width,
      layout.actions.rematch.height,
      '再戦',
      {
        fontSize: 20,
        fontColor: '#ffffff',
        bold: true,
        fillColor: 0x1e3a8a,
        strokeColor: 0x38bdf8,
        strokeWidth: 2,
        hoverFill: 0x2563eb,
        hoverStroke: 0x60a5fa,
      },
      () => this.scene.start('PreBattle'),
    );

    this.createActionButton(
      layout.actions.characterSelect.x,
      layout.actions.characterSelect.y,
      layout.actions.characterSelect.width,
      layout.actions.characterSelect.height,
      'キャラクター選択',
      {
        fontSize: 16,
        fontColor: '#e2e8f0',
        bold: true,
        fillColor: 0x172033,
        strokeColor: 0x334155,
        strokeWidth: 1.5,
        hoverFill: 0x1e293b,
        hoverStroke: 0x475569,
      },
      () => this.scene.start('CharacterSelect'),
    );

    this.createActionButton(
      layout.actions.title.x,
      layout.actions.title.y,
      layout.actions.title.width,
      layout.actions.title.height,
      'タイトルへ戻る',
      {
        fontSize: 14,
        fontColor: '#94a3b8',
        bold: false,
        fillColor: 0x0f172a,
        strokeColor: 0x1e293b,
        strokeWidth: 1,
        hoverFill: 0x172033,
        hoverStroke: 0x334155,
        hoverTextColor: '#cbd5e1',
      },
      () => this.scene.start('Title'),
    );

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      getPhase6Services().ads.hide();
    });
  }

  private createActionButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    style: {
      fontSize: number;
      fontColor: string;
      bold: boolean;
      fillColor: number;
      strokeColor: number;
      strokeWidth: number;
      hoverFill: number;
      hoverStroke: number;
      hoverTextColor?: string;
    },
    onClick: () => void,
  ): void {
    const halfW = w / 2;
    const halfH = h / 2;
    const bg = this.add.graphics();

    const draw = (fill: number, stroke: number): void => {
      bg.clear();
      bg.fillStyle(fill, 0.95);
      bg.fillRoundedRect(x - halfW, y - halfH, w, h, 4);
      bg.lineStyle(style.strokeWidth, stroke, 1);
      bg.strokeRoundedRect(x - halfW, y - halfH, w, h, 4);
    };

    draw(style.fillColor, style.strokeColor);

    const txt = this.add.text(x, y, label, {
      ...textStyle(style.fontSize),
      ...(style.bold ? { fontStyle: 'bold' } : {}),
      color: style.fontColor,
    }).setOrigin(0.5);

    const hitArea = this.add.zone(x, y, w, h).setInteractive({ useHandCursor: true });
    hitArea.on('pointerover', () => {
      draw(style.hoverFill, style.hoverStroke);
      if (style.hoverTextColor) txt.setColor(style.hoverTextColor);
    });
    hitArea.on('pointerout', () => {
      draw(style.fillColor, style.strokeColor);
      txt.setColor(style.fontColor);
    });
    hitArea.on('pointerdown', onClick);
  }

  private renderNightOfficeBackdrop(): void {
    const g = this.add.graphics().setDepth(-10);

    // Wall base
    g.fillStyle(0x070b16, 1);
    g.fillRect(0, 0, 1280, 620);

    // Window structure (Right side, X: 640..1180, Y: 40..380)
    g.fillStyle(0x030610, 1);
    g.fillRect(640, 40, 540, 340);

    // Distant city skyline behind window
    g.fillStyle(0x070d1a, 1);
    const buildings = [
      { x: 650, y: 150, w: 75, h: 230 },
      { x: 735, y: 110, w: 90, h: 270 },
      { x: 835, y: 180, w: 70, h: 200 },
      { x: 915, y: 90, w: 105, h: 290 },
      { x: 1030, y: 140, w: 85, h: 240 },
      { x: 1125, y: 190, w: 50, h: 190 },
    ];
    for (const b of buildings) g.fillRect(b.x, b.y, b.w, b.h);

    // Distant lights (Muted contrast)
    g.fillStyle(0xfacc15, 0.22);
    g.fillRect(755, 140, 6, 8);
    g.fillRect(780, 190, 6, 8);
    g.fillRect(945, 120, 8, 10);
    g.fillRect(975, 170, 8, 10);
    g.fillStyle(0x38bdf8, 0.2);
    g.fillRect(675, 200, 6, 8);
    g.fillRect(950, 220, 8, 10);
    g.fillRect(1055, 180, 6, 8);

    // Window frames
    g.lineStyle(2.5, 0x131c2e, 1);
    g.strokeRect(640, 40, 540, 340);
    g.lineBetween(820, 40, 820, 380);
    g.lineBetween(1000, 40, 1000, 380);
    g.lineBetween(640, 210, 1180, 210);

    // Wall Clock at (X: 1120, Y: 85) showing 02:00
    this.renderClock(g, 1120, 85, 20);

    // Floor Base (Y: 620..720)
    g.fillStyle(0x0f1624, 1);
    g.fillRect(0, 612, 1280, 8);
    g.lineStyle(2, 0x253347, 1);
    g.lineBetween(0, 620, 1280, 620);
    g.fillStyle(0x111726, 1);
    g.fillRect(0, 620, 1280, 100);
    g.lineStyle(1, 0x1a2436, 0.4);
    for (let x = 0; x < 1280; x += 80) g.lineBetween(x, 620, x - 40, 720);
    g.lineBetween(0, 670, 1280, 670);
  }

  private renderClock(g: Phaser.GameObjects.Graphics, x: number, y: number, radius: number): void {
    g.fillStyle(0x131c2e, 1);
    g.fillCircle(x, y, radius);
    g.lineStyle(1.5, 0x253347, 1);
    g.strokeCircle(x, y, radius);
    g.fillStyle(0x0a101d, 1);
    g.fillCircle(x, y, radius - 2.5);
    g.fillStyle(0x64748b, 0.8);
    g.fillRect(x - 1, y - radius + 3, 2, 3);
    g.fillRect(x + radius - 6, y - 1, 3, 2);
    g.fillRect(x - 1, y + radius - 6, 2, 3);
    g.fillRect(x - radius + 3, y - 1, 3, 2);
    // Minute hand pointing to 12
    g.lineStyle(1.5, 0xcbd5e1, 1);
    g.lineBetween(x, y, x, y - radius + 5);
    // Hour hand pointing to 2 (02:00)
    const hourLen = radius * 0.55;
    g.lineStyle(2, 0x38bdf8, 1);
    g.lineBetween(x, y, x + hourLen * 0.866, y - hourLen * 0.5);
    g.fillStyle(0x38bdf8, 1);
    g.fillCircle(x, y, 2);
  }
}

export const PHASE5_SCENES = [BootScene, LoadingScene, TitleScene, HowToPlayScene, CreditsScene, SettingsScene, KeyConfigScene, CharacterSelectScene, PreBattleScene, BattleScene, ResultScene];

export interface Phase5DebugApi {
  getScene(): string;
  getSceneTexts(): readonly string[];
  openScene(scene: string): void;
  start(mode: GameMode): void;
  select(p1: MvpCharacterId, p2?: MvpCharacterId): void;
  skipPreBattle(): void;
  getBattleState(): Readonly<{ snapshot: BattleSnapshot; battleSerial: number }> | null;
  finishRound(result: RoundResult): void;
  triggerRoundEnd(result: RoundResult): void;
  chooseResult(action: 'REMATCH' | 'CHARACTER_SELECT' | 'TITLE'): void;
  beginCinematicProbe(id: string): boolean;
  getCinematicProbeCompletions(): number;
  getPresentationState(): ReturnType<BattleScene['getVisualState']> | null;
}

const collectTexts = (obj: unknown): string[] => {
  if (!obj || typeof obj !== 'object') return [];
  const list: string[] = [];
  if ('text' in obj && typeof (obj as { text?: unknown }).text === 'string') {
    list.push((obj as { text: string }).text);
  }
  if ('list' in obj && Array.isArray((obj as { list?: unknown[] }).list)) {
    for (const child of (obj as { list: unknown[] }).list) {
      list.push(...collectTexts(child));
    }
  }
  return list;
};

export const installPhase5DebugApi = (game: Phaser.Game): Phase5DebugApi => {
  const active = (): Phaser.Scene | undefined => game.scene.getScenes(true)[0];
  const api: Phase5DebugApi = {
    getScene: () => active()?.sys.settings.key ?? '',
    getSceneTexts: () => (active() ? collectTexts(active()?.children) : []),
    openScene: (scene) => { active()?.scene.start(scene); },
    start: (mode) => { phase5Session.mode = mode; active()?.scene.start('CharacterSelect'); },
    select: (p1, p2) => { phase5Session.p1CharacterId = p1; phase5Session.p2CharacterId = phase5Session.mode === 'VS_CPU' ? otherCharacter(p1) : (p2 ?? p1); active()?.scene.start('PreBattle'); },
    skipPreBattle: () => active()?.scene.start('Battle'),
    getBattleState: () => { const scene = active(); return scene instanceof BattleScene ? Object.freeze({ snapshot: scene.getSnapshot(), battleSerial: phase5Session.battleSerial }) : null; },
    finishRound: (result) => { const scene = active(); if (scene instanceof BattleScene) scene.finishForTest(result); },
    triggerRoundEnd: (result) => { const scene = active(); if (scene instanceof BattleScene) scene.triggerRoundEnd(result); },
    chooseResult: (action) => active()?.scene.start(action === 'REMATCH' ? 'PreBattle' : action === 'CHARACTER_SELECT' ? 'CharacterSelect' : 'Title'),
    beginCinematicProbe: (id) => { const scene = active(); return scene instanceof BattleScene && scene.beginCinematicProbe(id); },
    getCinematicProbeCompletions: () => { const scene = active(); return scene instanceof BattleScene ? scene.getCinematicProbeCompletions() : 0; },
    getPresentationState: () => { const scene = active(); return scene instanceof BattleScene ? scene.getVisualState() : null; },
  };
  globalThis.officeClashPhase5 = api; return api;
};
